"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, FileText, Settings, User, Plus, Hash, Columns, Sun, Moon,
  FolderOpen, CheckSquare, Users, Bell, Shield, Clock,
  ArrowRight, CornerDownLeft, Network, PlusCircle, FilePlus, HelpCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/lib/workspace-context";
import { apiRequest } from "@/lib/api";
import { toast } from "@/components/ui/toast";
import { useRecentFilesStore } from "@/stores/recent-files-store";

interface CommandItem {
  id: string;
  type: "action" | "file";
  label: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  shortcut: string;
  handler: () => void;
}

export function CommandPalette() {
  const router = useRouter();
  const { 
    files, 
    openFile, 
    createFile, 
    setPaneViewType, 
    isSplit, 
    setIsSplit, 
    activePane 
  } = useWorkspace();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [apiHits, setApiHits] = useState<any[]>([]);

  // Refs para acceder a la lista más reciente desde el listener global.
  const itemsRef = useRef<CommandItem[]>([]);
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;

  // Monitor toggle keystroke (Cmd+K or Ctrl+K). Always active.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Single-key shortcuts ONLY when palette is closed. Prevents firing
  // when user is typing in the palette's own search input.
  useEffect(() => {
    if (isOpen) return;

    const isFormField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target.isContentEditable
      );
    };

    const down = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // No-op when palette is closed; the modal listens for this too
        return;
      }
      // Require Alt/Option key so normal typing or navigation never triggers actions accidentally
      if (isFormField(e.target) || !e.altKey || e.metaKey || e.ctrlKey) return;

      const key = e.key.toUpperCase();
      const list = itemsRef.current;
      if (!list) return;
      const action = list.find(
        (i) => i.type === "action" && i.shortcut && i.shortcut.toUpperCase() === key,
      );
      if (action) {
        e.preventDefault();
        action.handler();
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [isOpen]);

  // Debounced Search API query
  useEffect(() => {
    if (!search.trim()) {
      setApiHits([]);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      try {
        const response = await apiRequest<any>("/v1/search", {
          method: "POST",
          body: JSON.stringify({ q: search.trim(), limit: 10 })
        });
        if (response && response.hits) {
          setApiHits(response.hits);
        }
      } catch (err) {
        console.error("Search failed", err);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounce);
  }, [search]);

  // Filter commands and files based on search input.
  // Returns 3 groups: recent, actions, files. Each rendered with its own header.
  const grouped: { recent: CommandItem[]; actions: CommandItem[]; files: CommandItem[] } = useMemo(() => {
    const navTo = (href: string) => () => {
      router.push(href);
    };

    const actions: CommandItem[] = [
      // ── Crear / Modificar ──
      {
        id: "create-note",
        type: "action",
        label: "Crear nueva nota",
        icon: FilePlus,
        shortcut: "N",
        handler: () => {
          const newId = createFile("Nota sin título");
          openFile(newId, activePane);
          toast.success("Nota creada", "Se creó una nueva nota sin título.");
        }
      },
      {
        id: "toggle-split",
        type: "action",
        label: isSplit ? "Unir paneles (cerrar división)" : "Dividir pantalla verticalmente",
        icon: Columns,
        shortcut: "D",
        handler: () => setIsSplit(!isSplit)
      },
      {
        id: "open-graph",
        type: "action",
        label: "Abrir grafo de conocimiento",
        icon: Network,
        shortcut: "G",
        handler: () => setPaneViewType("graph", activePane)
      },
      {
        id: "toggle-theme",
        type: "action",
        label: "Cambiar tema (claro / oscuro)",
        icon: Sun,
        shortcut: "T",
        handler: () => {
          const isDark = document.documentElement.classList.toggle("dark");
          localStorage.setItem("wiki_theme", isDark ? "dark" : "light");
        }
      },
      // ── Navegación ──
      {
        id: "nav-projects",
        type: "action",
        label: "Ir a Proyectos",
        icon: ArrowRight,
        shortcut: "",
        handler: navTo("/workspace/spaces"),
      },
      {
        id: "nav-tasks",
        type: "action",
        label: "Ir a Tareas (tablero Kanban)",
        icon: ArrowRight,
        shortcut: "",
        handler: navTo("/workspace/tasks"),
      },
      {
        id: "nav-members",
        type: "action",
        label: "Ir a Miembros del equipo",
        icon: ArrowRight,
        shortcut: "",
        handler: navTo("/workspace/users"),
      },
      {
        id: "nav-notifications",
        type: "action",
        label: "Ir a Notificaciones",
        icon: ArrowRight,
        shortcut: "",
        handler: navTo("/workspace/notifications"),
      },
      {
        id: "nav-search",
        type: "action",
        label: "Ir a Búsqueda",
        icon: ArrowRight,
        shortcut: "",
        handler: navTo("/workspace/search"),
      },
      {
        id: "nav-templates",
        type: "action",
        label: "Ir a Plantillas",
        icon: ArrowRight,
        shortcut: "",
        handler: navTo("/workspace/templates"),
      },
      {
        id: "nav-admin",
        type: "action",
        label: "Ir a Administración",
        icon: ArrowRight,
        shortcut: "",
        handler: navTo("/workspace/admin"),
      },
      // ── Sistema ──
      {
        id: "open-help",
        type: "action",
        label: "Abrir Centro de Ayuda",
        icon: HelpCircle,
        shortcut: "?",
        handler: () => {
          setIsOpen(false); // Close the command palette
          setTimeout(() => {
            document.dispatchEvent(new KeyboardEvent("keydown", { key: "?" }));
          }, 100);
        }
      },
      {
        id: "open-settings",
        type: "action",
        label: "Abrir configuración",
        icon: Settings,
        shortcut: ",",
        handler: () => setPaneViewType("settings", activePane)
      },
    ];

    const matchedActions = actions.filter(action => 
      action.label.toLowerCase().includes(search.toLowerCase())
    );

    const matchedFiles: CommandItem[] = search.trim() !== "" && apiHits.length > 0
      ? apiHits
          .map((hit): CommandItem | null => {
            const f = files.find((file) => file.documentId === hit.document_id);
            if (!f) return null;
            return {
              id: f.id,
              type: "file",
              label: `Abrir nota: ${f.name.replace(".md", "")}`,
              icon: FileText,
              shortcut: "",
              handler: () => openFile(f.id, activePane),
            };
          })
          .filter((f): f is CommandItem => f !== null)
      : files
          .filter((f) => f.type === "file" && f.name.toLowerCase().includes(search.toLowerCase()))
          .map((f): CommandItem => ({
            id: f.id,
            type: "file",
            label: `Abrir nota: ${f.name.replace(".md", "")}`,
            icon: FileText,
            shortcut: "",
            handler: () => openFile(f.id, activePane),
          }));

    // Recent files (only when no query)
    const recentItems: CommandItem[] =
      search.trim() === ""
        ? useRecentFilesStore
            .getState()
            .topN(5)
            .map((e): CommandItem | null => {
              const f = files.find((file) => file.id === e.fileId);
              if (!f) return null;
              return {
                id: `recent-${f.id}`,
                type: "file",
                label: f.name.replace(".md", ""),
                icon: Clock,
                shortcut: "",
                handler: () => openFile(f.id, activePane),
              };
            })
            .filter((x): x is CommandItem => x !== null)
        : [];

    return { recent: recentItems, actions: matchedActions, files: matchedFiles };
  }, [search, files, apiHits, isSplit, activePane, openFile, createFile, setPaneViewType, setIsSplit, router]);

  const items = useMemo<CommandItem[]>(
    () => [...grouped.recent, ...grouped.actions, ...grouped.files],
    [grouped],
  );

  itemsRef.current = items;

  // Reset selected item index on query change
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (items[selectedIndex]) {
        items[selectedIndex].handler();
        setIsOpen(false);
        setSearch("");
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-[100] bg-background/50 backdrop-blur-md"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -20 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-1/2 top-[15%] z-[101] w-full max-w-xl -translate-x-1/2 rounded-2xl bg-card border border-border/50 shadow-2xl overflow-hidden"
            onKeyDown={handleKeyDown}
          >
            {/* Search Input */}
            <div className="flex items-center px-4 py-3.5 border-b border-border/10">
              <Search className="w-4 h-4 text-muted-foreground/70 mr-3" strokeWidth={1.75} />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Busca notas o ejecuta comandos…"
                className="flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground/40 text-sm font-medium tracking-tight"
              />
              <kbd className="px-1.5 py-0.5 rounded bg-secondary/60 border border-border/50 text-[10px] font-mono text-muted-foreground">
                ESC
              </kbd>
            </div>

            {/* Results / Options */}
            <div className="p-2 max-h-[380px] overflow-y-auto custom-scrollbar">
              <div className="space-y-[2px]">
                {/* Recent files section */}
                {grouped.recent.length > 0 && (
                  <CommandGroup
                    title={search ? "Coincidencias en recientes" : "Recientes"}
                    items={grouped.recent}
                    selectedIndex={selectedIndex}
                    onSelect={(i) => {
                      setSelectedIndex(i);
                      const item = grouped.recent[i];
                      if (item) {
                        item.handler();
                        setIsOpen(false);
                        setSearch("");
                      }
                    }}
                    onHover={setSelectedIndex}
                    getItemIndex={(item) => items.findIndex((it) => it.id === item.id)}
                  />
                )}

                {/* Actions section */}
                {grouped.actions.length > 0 && (
                  <CommandGroup
                    title={search ? "Comandos" : "Acciones"}
                    items={grouped.actions}
                    selectedIndex={selectedIndex}
                    onSelect={(i) => {
                      setSelectedIndex(i);
                      const item = grouped.actions[i];
                      if (item) {
                        item.handler();
                        setIsOpen(false);
                        setSearch("");
                      }
                    }}
                    onHover={setSelectedIndex}
                    getItemIndex={(item) => items.findIndex((it) => it.id === item.id)}
                  />
                )}

                {/* Files section */}
                {grouped.files.length > 0 && (
                  <CommandGroup
                    title="Notas"
                    items={grouped.files}
                    selectedIndex={selectedIndex}
                    onSelect={(i) => {
                      setSelectedIndex(i);
                      const item = grouped.files[i];
                      if (item) {
                        item.handler();
                        setIsOpen(false);
                        setSearch("");
                      }
                    }}
                    onHover={setSelectedIndex}
                    getItemIndex={(item) => items.findIndex((it) => it.id === item.id)}
                  />
                )}

                {items.length === 0 && (
                  <div className="py-8 text-center text-sm text-muted-foreground/60">
                    {search ? (
                      <>Sin resultados para “{search}”</>
                    ) : (
                      "Escribe para buscar…"
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-4 py-2.5 bg-secondary/10 border-t border-border/10 flex items-center justify-between text-[10px] text-muted-foreground/60">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5">
                  <kbd className="px-1 py-0.5 rounded border border-border/50 bg-background/60 text-[9px] font-mono">↑↓</kbd>
                  navegar
                </span>
                <span className="flex items-center gap-1.5">
                  <kbd className="px-1 py-0.5 rounded border border-border/50 bg-background/60 text-[9px] font-mono">↵</kbd>
                  seleccionar
                </span>
                <span className="flex items-center gap-1.5">
                  <kbd className="px-1 py-0.5 rounded border border-border/50 bg-background/60 text-[9px] font-mono">?</kbd>
                  atajos
                </span>
              </div>
              <span className="font-mono text-[9px]">Arsenal Wiki</span>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface CommandGroupProps {
  title: string;
  items: CommandItem[];
  selectedIndex: number;
  onSelect: (groupIndex: number) => void;
  onHover: (globalIndex: number) => void;
  getItemIndex: (item: CommandItem) => number;
}

function CommandGroup({ title, items, selectedIndex, onSelect, onHover, getItemIndex }: CommandGroupProps) {
  return (
    <>
      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
        {title}
      </div>
      {items.map((item, i) => {
        const globalIndex = getItemIndex(item);
        const isSelected = globalIndex === selectedIndex;
        return (
          <button
            key={item.id}
            onMouseEnter={() => onHover(globalIndex)}
            onClick={() => onSelect(i)}
            className={cn(
              "w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-75 text-left active:scale-[0.99]",
              isSelected
                ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20"
                : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              <item.icon
                className={cn(
                  "w-4 h-4 shrink-0",
                  isSelected ? "text-primary-foreground" : "text-muted-foreground/70",
                )}
                strokeWidth={1.75}
              />
              <span
                className={cn(
                  "text-[13px] font-medium truncate",
                  isSelected ? "text-primary-foreground" : "text-foreground",
                )}
              >
                {item.label}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {item.shortcut && (
                <kbd
                  className={cn(
                    "px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold border transition-colors",
                    isSelected
                      ? "bg-primary-foreground/15 border-primary-foreground/20 text-primary-foreground"
                      : "bg-secondary border-border/50 text-muted-foreground",
                  )}
                >
                  {item.shortcut}
                </kbd>
              )}
            </div>
          </button>
        );
      })}
    </>
  );
}
